import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Deck, Card } from "@/types/linguadeck";
import { useAuth } from "@/contexts/AuthContext";
import { useGamification } from "@/contexts/GamificationContext";
import { upsertProgress, recordCardInteraction } from "@/lib/db";

function pickRandomCard(cards: Card[], excludeId?: string): Card | null {
  const pool = excludeId ? cards.filter((c) => c.id !== excludeId) : cards;
  if (pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)];
}

interface ExpressReviewWidgetProps {
  deck: Deck;
}

export default function ExpressReviewWidget({ deck }: ExpressReviewWidgetProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { finishStudySession } = useGamification();
  const [card, setCard] = useState<Card | null>(() => pickRandomCard(deck.cards ?? []));
  const [flipped, setFlipped] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const cards = deck.cards ?? [];
  const hasCards = cards.length > 0;

  useEffect(() => {
    setCard(pickRandomCard(cards));
    setFlipped(false);
  }, [deck.id]);

  const loadNextCard = useCallback(() => {
    if (!card) return;
    setIsTransitioning(true);
    setFlipped(false);
    setTimeout(() => {
      const next = pickRandomCard(cards, card.id);
      setCard(next ?? pickRandomCard(cards));
      setIsTransitioning(false);
    }, 150);
  }, [card, cards]);

  const handleMark = useCallback(
    async (correct: boolean) => {
      if (!card || !user?.id) return;
      try {
        await upsertProgress(user.id, card.id, correct);
        if (deck.id) {
          recordCardInteraction({
            userId: user.id,
            cardId: card.id,
            deckId: deck.id,
            mode: "cards",
            answeredCorrectly: correct,
          }).catch((e) => console.warn("[ExpressReview] recordCardInteraction failed:", e));
        }
        finishStudySession(1, false);
      } catch (e) {
        console.warn("[ExpressReview] Failed to save progress:", e);
      }
      loadNextCard();
    },
    [card, user?.id, deck.id, finishStudySession, loadNextCard]
  );

  const displayText = useMemo(() => {
    if (!card) return "";
    return flipped ? (card.back || "").trim() : (card.front || "").trim();
  }, [card, flipped]);

  if (!hasCards) return null;

  return (
    <div className="w-full rounded-xl border border-slate-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] shadow-sm overflow-hidden">
      <p className="text-xs font-medium text-slate-500 dark:text-[hsl(var(--muted-foreground))] px-3 pt-2.5 pb-1">
        {t("deckDashboard.expressReview")}
      </p>
      <div className="px-3 pb-3">
        <div
          role="button"
          tabIndex={0}
          onClick={() => !isTransitioning && setFlipped((f) => !f)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              !isTransitioning && setFlipped((f) => !f);
            }
          }}
          className="min-h-[72px] rounded-lg border border-slate-100 dark:border-[hsl(var(--border))] bg-slate-50 dark:bg-[hsl(var(--bg3))] p-4 cursor-pointer hover:border-slate-200 dark:hover:border-[hsl(var(--border))] transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500/50"
          aria-label={t("deckDashboard.tapToFlip")}
        >
          <AnimatePresence mode="wait">
            <motion.p
              key={`${card?.id}-${flipped}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="text-base font-medium text-slate-800 dark:text-[hsl(var(--foreground))] text-center leading-relaxed"
            >
              {displayText || t("deckDashboard.emptyCard")}
            </motion.p>
          </AnimatePresence>
          <p className="text-[11px] text-slate-400 dark:text-[hsl(var(--muted-foreground))] mt-2 text-center">
            {t("deckDashboard.tapToFlip")}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <button
            type="button"
            onClick={() => handleMark(false)}
            disabled={!user || isTransitioning}
            className="min-h-[52px] py-4 px-3 rounded-lg text-base font-semibold bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 hover:bg-rose-200 dark:hover:bg-rose-900/50 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("deckDashboard.dontKnow")}
          </button>
          <button
            type="button"
            onClick={() => handleMark(true)}
            disabled={!user || isTransitioning}
            className="min-h-[52px] py-4 px-3 rounded-lg text-base font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("deckDashboard.know")}
          </button>
        </div>
      </div>
    </div>
  );
}
